import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined';
import CallReceivedOutlinedIcon from '@mui/icons-material/CallReceivedOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ConveyorBeltIcon from '@mui/icons-material/ConveyorBelt';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GrassOutlinedIcon from '@mui/icons-material/GrassOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MonitorWeightOutlinedIcon from '@mui/icons-material/MonitorWeightOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import { CattleIcon } from '@components/Icons/CattleIcon';
import { CochoPastureIcon } from '@components/Icons/CochoPastureIcon';
import { appRoutes } from '@routes/appRoutes';

/**
 * Organização da sidebar por fluxo de trabalho da fazenda:
 *
 * 1. Visão Geral   — onde o dia começa: painel e relatórios.
 * 2. Rebanho       — o ativo principal: animais, lotes, manejo e avaliações.
 * 3. Operação de Campo — rotina diária no campo: tarefas, pasto, cocho, sanitário.
 * 4. Nutrição e Produção — cadeia alimentar completa: do ingrediente ao estoque.
 * 5. Comercial     — entrada e saída de dinheiro: compra, venda e parceiros.
 * 6. Administração — cadastros estruturais e acesso ao sistema.
 *
 * O Financeiro (`/financeiro`) é uma segunda área da mesma navegação, aberta
 * pelo seletor Gestão / Financeiro do topo: a barra lateral mostra só as
 * seções da área da rota atual (`getNavigationArea`). Seis grupos fixos —
 * plano UX do Financeiro §3; cada grupo aparece quando tem item entregue.
 */
export type NavigationArea = 'management' | 'financial';

export type NavigationSectionKey =
  | 'overview'
  | 'herd'
  | 'operation'
  | 'nutrition'
  | 'commercial'
  | 'admin'
  | 'financialOverview'
  | 'financialAccounts'
  | 'financialStock'
  | 'financialCosts'
  | 'financialResults'
  | 'financialRegisters';

export type NavigationRole = 'view' | 'create' | 'del' | 'upgrade';
export type LicenseScope = 'CONFINEMENT' | 'PASTURE' | 'SHARED';
export type LicenseFeature = 'automation';
export type NavigationAccessLevel = 'admin' | 'leader' | 'normal';

export interface NavigationSection {
  key: NavigationSectionKey;
  label: string;
  order: number;
  area: NavigationArea;
}

export interface NavigationItem {
  key: string;
  label: string;
  route: string;
  icon: React.ElementType;
  section: NavigationSectionKey;
  order: number;
  module?: string;
  role?: NavigationRole;
  accessLevels?: NavigationAccessLevel[];
  farmRequired?: boolean;
  licenseScope: LicenseScope;
  /** Feature adicional exigida na licença global. Ausente = sempre visível. */
  licenseFeature?: LicenseFeature;
  searchKeywords: string[];
  activePrefixes?: string[];
}

export const navigationSections: NavigationSection[] = [
  { key: 'overview', label: 'Visão Geral', order: 1, area: 'management' },
  { key: 'herd', label: 'Rebanho', order: 2, area: 'management' },
  { key: 'operation', label: 'Operação de Campo', order: 3, area: 'management' },
  { key: 'nutrition', label: 'Nutrição e Produção', order: 4, area: 'management' },
  { key: 'commercial', label: 'Comercial', order: 5, area: 'management' },
  { key: 'admin', label: 'Administração', order: 6, area: 'management' },
  { key: 'financialOverview', label: 'Visão geral', order: 1, area: 'financial' },
  { key: 'financialAccounts', label: 'Contas', order: 2, area: 'financial' },
  { key: 'financialStock', label: 'Estoque', order: 3, area: 'financial' },
  { key: 'financialCosts', label: 'Custos', order: 4, area: 'financial' },
  { key: 'financialResults', label: 'Resultados', order: 5, area: 'financial' },
  { key: 'financialRegisters', label: 'Cadastros', order: 6, area: 'financial' },
];

export const navigationItems: NavigationItem[] = [
  // ── Visão Geral ──────────────────────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Dashboard',
    route: appRoutes.dashboard,
    icon: DashboardOutlinedIcon,
    section: 'overview',
    order: 1,
    licenseScope: 'SHARED',
    searchKeywords: ['inicio', 'visao geral', 'painel'],
  },
  {
    key: 'relatorios',
    label: 'Relatórios',
    route: appRoutes.relatorios,
    icon: InsightsOutlinedIcon,
    section: 'overview',
    order: 2,
    module: 'report',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['analitico', 'operacional', 'indicadores'],
  },

  // ── Rebanho ──────────────────────────────────────────────────────────────
  {
    key: 'animais',
    label: 'Animais',
    route: appRoutes.animais.list,
    icon: CattleIcon,
    section: 'herd',
    order: 1,
    module: 'animal',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['gado', 'brinco', 'cadastro'],
    activePrefixes: [appRoutes.animais.list],
  },
  {
    key: 'lotes',
    label: 'Lotes',
    route: appRoutes.lotes.list,
    icon: LayersOutlinedIcon,
    section: 'herd',
    order: 2,
    module: 'lot',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['grupos', 'curral', 'area'],
    activePrefixes: [appRoutes.lotes.list],
  },
  {
    key: 'areas',
    label: 'Áreas',
    route: appRoutes.areas.list,
    icon: MapOutlinedIcon,
    section: 'herd',
    order: 3,
    module: 'area',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['setores', 'mapa', 'talhoes'],
    activePrefixes: [appRoutes.areas.list],
  },
  {
    key: 'manejo',
    label: 'Manejo',
    route: appRoutes.manejo.list,
    icon: MonitorWeightOutlinedIcon,
    section: 'herd',
    order: 4,
    module: 'handling',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['manejo', 'pesagem', 'apartacao', 'apartação', 'fase', 'transferencia', 'transferência', 'curral'],
    activePrefixes: [appRoutes.manejo.list],
  },
  {
    key: 'avaliacoes',
    label: 'Avaliações',
    route: appRoutes.avaliacoes.list,
    icon: QueryStatsOutlinedIcon,
    section: 'herd',
    order: 5,
    module: 'assessment',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['pesagem', 'desempenho', 'analise', 'avaliação de venda', 'avaliações de venda', 'lote de saída', 'lotes de saída'],
    activePrefixes: [appRoutes.avaliacoes.list],
  },
  {
    key: 'movimentacoes',
    label: 'Movimentação',
    route: appRoutes.movimentacoes.list,
    icon: HistoryOutlinedIcon,
    section: 'herd',
    order: 6,
    module: 'lot',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['movimentacao', 'movimentação', 'historico', 'histórico', 'lote', 'curral', 'cancelar'],
    activePrefixes: [appRoutes.movimentacoes.list],
  },

  // ── Operação de Campo ────────────────────────────────────────────────────
  {
    key: 'operacao',
    label: 'Operação',
    route: appRoutes.operacao.list,
    icon: TaskAltOutlinedIcon,
    section: 'operation',
    order: 1,
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['operacao', 'operação', 'tarefas', 'diario', 'rotina'],
    activePrefixes: [appRoutes.operacao.list],
  },
  {
    key: 'pasto',
    label: 'Pasto',
    route: appRoutes.pasto,
    icon: GrassOutlinedIcon,
    section: 'operation',
    order: 2,
    module: 'pasture',
    role: 'view',
    farmRequired: true,
    licenseScope: 'PASTURE',
    searchKeywords: ['pasto', 'piquete', 'manejo', 'rotacionado', 'continuo', 'rotacao', 'descanso', 'capim', 'ocupacao'],
  },
  {
    key: 'cocho',
    label: 'Cocho',
    route: appRoutes.cocho.list,
    icon: CochoPastureIcon,
    section: 'operation',
    order: 3,
    module: 'nutrition',
    role: 'view',
    farmRequired: true,
    // Cocho é exclusivo de confinamento (decisão de produto, auditoria §5.5)
    // — mesmo escopo do backend (CochoController = CONFINEMENT).
    licenseScope: 'CONFINEMENT',
    searchKeywords: ['cocho', 'leitura', 'noturna', 'sobra', 'incidente', 'clima'],
    activePrefixes: ['/cocho'],
  },
  {
    key: 'sanitario',
    label: 'Sanitário',
    route: appRoutes.sanitario.list,
    icon: VaccinesOutlinedIcon,
    section: 'operation',
    order: 4,
    module: 'sanitary',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['sanitario', 'vacina', 'tratamento', 'vermifugo', 'protocolo'],
    activePrefixes: [appRoutes.sanitario.list],
  },

  // ── Nutrição e Produção ──────────────────────────────────────────────────
  {
    key: 'nutricao',
    label: 'Nutrição',
    route: appRoutes.nutricao.list,
    icon: RestaurantOutlinedIcon,
    section: 'nutrition',
    order: 1,
    module: 'nutrition',
    role: 'view',
    farmRequired: true,
    // Nutrição vale para todas as licenças: o pasto suplementado (sal,
    // proteico, TIP) tem rotina diária de nutrição (auditoria §5.5).
    licenseScope: 'SHARED',
    searchKeywords: ['cocho', 'ajuste diario', 'previsao', 'cms'],
    activePrefixes: [appRoutes.nutricao.list],
  },
  {
    key: 'automacao',
    label: 'Automação de Vagão',
    route: appRoutes.automacao.list,
    // Este item representa o fluxo automatizado de transporte da mistura;
    // manufatura fica reservada ao módulo de Produção.
    icon: ConveyorBeltIcon,
    section: 'nutrition',
    order: 2,
    module: 'automation',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    // Feature adicional da licença: instalação sem ela não vê nem o item.
    licenseFeature: 'automation',
    searchKeywords: ['vagao', 'misturador', 'carga', 'balanca', 'tmr'],
    activePrefixes: [appRoutes.automacao.list],
  },
  {
    key: 'ingredientes',
    label: 'Ingredientes',
    route: appRoutes.ingredientes.list,
    icon: ScienceOutlinedIcon,
    section: 'nutrition',
    order: 3,
    module: 'item',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['insumos', 'materia-prima', 'itens'],
    activePrefixes: [appRoutes.ingredientes.list],
  },
  {
    key: 'formulas',
    label: 'Fórmulas',
    route: appRoutes.formulas.list,
    icon: MenuBookOutlinedIcon,
    section: 'nutrition',
    order: 4,
    module: 'diet',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['dieta', 'receita', 'formula'],
    activePrefixes: [appRoutes.formulas.list],
  },
  {
    key: 'programas',
    label: 'Programas',
    route: appRoutes.programas.list,
    icon: HubOutlinedIcon,
    section: 'nutrition',
    order: 5,
    module: 'program',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['planejamento', 'etapas', 'cms'],
    activePrefixes: [appRoutes.programas.list],
  },
  {
    key: 'curvas-de-ganho',
    label: 'Curvas de Ganho',
    route: appRoutes.curvasDeGanho.list,
    icon: ShowChartOutlinedIcon,
    section: 'nutrition',
    order: 6,
    module: 'gainCurve',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['ganho', 'curva', 'crescimento'],
    activePrefixes: [appRoutes.curvasDeGanho.list],
  },
  {
    key: 'producao',
    label: 'Produção',
    route: appRoutes.producao.list,
    icon: PrecisionManufacturingOutlinedIcon,
    section: 'nutrition',
    order: 7,
    module: 'production',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['fabricacao', 'mistura', 'premix'],
    activePrefixes: [appRoutes.producao.list],
  },

  // ── Comercial ────────────────────────────────────────────────────────────
  {
    key: 'contratos',
    label: 'Contrato',
    route: appRoutes.contratos.list,
    icon: ShoppingCartOutlinedIcon,
    section: 'commercial',
    order: 1,
    module: 'contract',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['contrato', 'contratos', 'compra', 'compras', 'aquisicao', 'fornecedor', 'recebimento', 'gta'],
    activePrefixes: [appRoutes.contratos.list],
  },
  {
    key: 'financas',
    label: 'Vendas',
    route: appRoutes.financas.list,
    icon: AttachMoneyOutlinedIcon,
    section: 'commercial',
    order: 2,
    module: 'finance',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'receita', 'abate'],
    activePrefixes: [appRoutes.financas.list],
  },
  {
    key: 'transferencias',
    label: 'Transferências',
    route: appRoutes.transferencias.list,
    icon: SwapHorizOutlinedIcon,
    section: 'commercial',
    order: 3,
    module: 'transfer',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['transferencia', 'transferência', 'entre fazendas', 'transito', 'trânsito', 'recebimento', 'movimentacao'],
    activePrefixes: [appRoutes.transferencias.list],
  },
  {
    key: 'financas-detalhe',
    label: 'Resultado das saídas',
    route: appRoutes.financasDetalhe,
    icon: AccountBalanceWalletOutlinedIcon,
    section: 'commercial',
    order: 4,
    module: 'finance',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['saidas', 'vendas', 'mortes', 'resultado', 'margem'],
    activePrefixes: [appRoutes.financasDetalhe],
  },
  // ── Administração ────────────────────────────────────────────────────────
  {
    key: 'fazendas',
    label: 'Fazendas',
    route: appRoutes.fazendas.list,
    icon: HomeWorkOutlinedIcon,
    section: 'admin',
    order: 1,
    module: 'farm',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['unidades', 'propriedades'],
    activePrefixes: [appRoutes.fazendas.list],
  },
  {
    key: 'usuarios',
    label: 'Usuários',
    route: appRoutes.usuarios.list,
    icon: PeopleOutlineOutlinedIcon,
    section: 'admin',
    order: 2,
    module: 'user',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['acessos', 'permissoes', 'equipe'],
    activePrefixes: [appRoutes.usuarios.list],
  },
  {
    key: 'automacaoPerfis',
    label: 'Perfis de balança',
    route: appRoutes.automacao.perfisTecnicos,
    icon: ScaleOutlinedIcon,
    section: 'admin',
    order: 4,
    // Só admin: publicar ou homologar perfil decide qual peso vira consumo
    // na Nutrição. Não é tela de gestor de fazenda.
    accessLevels: ['admin'],
    licenseScope: 'SHARED',
    licenseFeature: 'automation',
    searchKeywords: ['perfil', 'balanca', 'indicador', 'homologacao'],
    activePrefixes: [appRoutes.automacao.perfisTecnicos],
  },
  {
    key: 'licencas',
    label: 'Licencas',
    route: appRoutes.licencas,
    icon: AdminPanelSettingsOutlinedIcon,
    section: 'admin',
    order: 3,
    accessLevels: ['admin'],
    licenseScope: 'SHARED',
    searchKeywords: ['licencas', 'licenca', 'plano', 'modo'],
    activePrefixes: [appRoutes.licencas],
  },

  // ── Financeiro (área própria) ───────────────────────────────────────────
  {
    key: 'financeiro-resumo',
    label: 'Resumo',
    route: appRoutes.financeiro.resumo,
    icon: SpaceDashboardOutlinedIcon,
    section: 'financialOverview',
    order: 1,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'resumo', 'visao geral', 'consolidado', 'vencidas', 'fluxo de caixa'],
  },
  {
    key: 'financeiro-caixa',
    label: 'Caixa',
    route: appRoutes.financeiro.caixa,
    icon: PointOfSaleOutlinedIcon,
    section: 'financialAccounts',
    order: 1,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'caixa', 'saldo', 'extrato', 'transferencia entre contas', 'banco'],
  },
  {
    key: 'financeiro-a-pagar',
    label: 'A pagar',
    route: appRoutes.financeiro.aPagar,
    icon: CallMadeOutlinedIcon,
    section: 'financialAccounts',
    order: 2,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'contas a pagar', 'boleto', 'fornecedor', 'vencimento', 'despesa'],
  },
  {
    key: 'financeiro-a-receber',
    label: 'A receber',
    route: appRoutes.financeiro.aReceber,
    icon: CallReceivedOutlinedIcon,
    section: 'financialAccounts',
    order: 3,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'contas a receber', 'recebimento', 'comprador', 'vencimento', 'receita'],
  },
  {
    key: 'financeiro-compras',
    label: 'Compras e pagamentos',
    route: appRoutes.financeiro.compras,
    icon: ShoppingBasketOutlinedIcon,
    section: 'financialAccounts',
    order: 4,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'compras', 'compra de produtos', 'compra de animais', 'frete', 'nota fiscal', 'recebimento', 'pedido'],
  },
  {
    key: 'financeiro-notas-recebidas',
    label: 'Notas recebidas',
    route: appRoutes.financeiro.notasRecebidas,
    icon: ReceiptLongOutlinedIcon,
    section: 'financialAccounts',
    order: 5,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'xml', 'nota fiscal', 'nfe', 'notas recebidas'],
  },
  {
    // Única tela de estoque (saiu da Gestão), com a permissão de estoque (não a
    // financeira): quem só opera estoque entra no Financeiro só por aqui (plano
    // técnico §6.2). Compra de fornecedor entra por Compras e pagamentos.
    key: 'financeiro-estoque',
    label: 'Controle de Estoque',
    route: appRoutes.financeiro.estoque,
    icon: Inventory2OutlinedIcon,
    section: 'financialStock',
    order: 1,
    module: 'inventory',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['estoque', 'ingredientes', 'sanitario', 'contagem', 'perda', 'saldo', 'inventario', 'combustivel', 'diesel', 'tanque', 'abastecimento'],
  },
  {
    key: 'financeiro-centros',
    label: 'Centros de custo',
    route: appRoutes.financeiro.centros,
    activePrefixes: [appRoutes.financeiro.centros, appRoutes.financeiro.subcentros],
    icon: AccountTreeOutlinedIcon,
    section: 'financialCosts',
    order: 1,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'centro de custo', 'subcentro', 'rateio', 'pasto', 'confinamento', 'lavoura'],
  },
  {
    key: 'financeiro-projetos',
    label: 'Projetos',
    route: appRoutes.financeiro.projetos,
    icon: WorkOutlineOutlinedIcon,
    section: 'financialCosts',
    order: 1.5,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'projeto', 'obra', 'reforma', 'orcamento', 'orçado', 'comprometido'],
  },
  {
    key: 'financeiro-custeio',
    label: 'Conferência da diária',
    route: appRoutes.financeiro.custeio,
    icon: CalculateOutlinedIcon,
    section: 'financialCosts',
    order: 2,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['custeio', 'diaria', 'cabeca dia', 'rateio', 'custo por animal', 'pasto', 'confinamento', 'operacional', 'custo automatico', 'nao absorvido'],
  },
  {
    key: 'financeiro-dre',
    label: 'DRE gerencial',
    route: appRoutes.financeiro.dre,
    icon: AssessmentOutlinedIcon,
    section: 'financialResults',
    order: 1,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['dre', 'resultado', 'lucro', 'margem', 'resultado liquido', 'custo dos animais vendidos'],
  },
  {
    key: 'financeiro-custos-do-periodo',
    label: 'Custos e receitas do período',
    route: appRoutes.financeiro.custosDoPeriodo,
    icon: StackedBarChartOutlinedIcon,
    section: 'financialResults',
    order: 2,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['custos do periodo', 'ajuste do rebanho', 'ponte do rebanho', 'custo gerado', 'receitas'],
  },
  {
    key: 'compradores',
    label: 'Compradores',
    route: appRoutes.compradores.list,
    icon: StorefrontOutlinedIcon,
    section: 'financialRegisters',
    order: 1,
    module: 'buyer',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['clientes', 'compras'],
    activePrefixes: [appRoutes.compradores.list],
  },
  {
    key: 'fornecedores',
    label: 'Fornecedores',
    route: appRoutes.fornecedores.list,
    icon: LocalShippingOutlinedIcon,
    section: 'financialRegisters',
    order: 2,
    module: 'supplier',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['parceiros', 'suprimentos', 'insumos'],
    activePrefixes: [appRoutes.fornecedores.list],
  },
  {
    key: 'financeiro-contas-financeiras',
    label: 'Contas financeiras',
    route: appRoutes.financeiro.contasFinanceiras,
    icon: AccountBalanceOutlinedIcon,
    section: 'financialRegisters',
    order: 3,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'banco', 'caixa', 'conta bancaria', 'saldo'],
  },
  {
    key: 'financeiro-categorias',
    label: 'Categorias financeiras',
    route: appRoutes.financeiro.categorias,
    icon: CategoryOutlinedIcon,
    section: 'financialRegisters',
    order: 4,
    module: 'financialManagement',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'categoria', 'despesa', 'receita', 'dre'],
  },
];
